<template>
  <div class="participant">
    <div class="participant__avatar">
      <img
        :src="participant.avatar?.data || participant.avatarPlaceholder"
        :alt="participantName"
        loading="lazy"
        fetchpriority="low"
      />
    </div>
    <div class="participant__content">
      <div class="participant__info">
        <p class="participant__name">{{ participantName.trim() }}</p>
        <span class="participant__payment-date">{{ paymentDate }}</span>
      </div>
      <div class="participant__amount">
        <div>${{ totalDonation.toLocaleString(locale) }}</div>
        <div v-if="amountSums" class="participant__amount-sum">
          {{ amountSums }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  fromUnixTime,
  isToday,
  isYesterday,
} from 'date-fns';

const { $t } = useI18n();
const { participant, locale } = defineProps({
  participant: {
    type: Object,
    required: true,
  },
  locale: {
    type: String,
    default: 'en',
  },
});

const participantName = computed(() => {
  if (
    (!participant?.firstName || participant?.firstName === 'anonymous') &&
    !participant?.lastName
  )
    return 'Anonymous';
  return `${participant?.firstName} ${participant?.lastName}`;
});

const paymentDate = computed(() => {
  return formatRelativeDate(participant.donatedAt);
});

const donation = computed(() => {
  return participant.donation % 100
    ? (participant.donation / 100).toFixed(2)
    : participant.donation / 100;
});

const doubled = computed(() => {
  return participant.doubled % 100
    ? (participant.doubled / 100).toFixed(2)
    : participant.doubled / 100;
});

const promotion = computed(() => {
  return participant.promotion % 100
    ? (participant.promotion / 100).toFixed(2)
    : participant.promotion / 100;
});

const totalDonation = computed(() => {
  return donation.value + doubled.value + promotion.value;
});

const amountSums = computed(() => {
  const sums = [`$${donation.value}`];

  if (doubled.value) {
    sums.push(`+$${doubled.value}`);
  }

  if (promotion.value) {
    sums.push(`+$${promotion.value}`);
  }

  return sums.length > 1 ? sums.join('') : null;
});

function formatRelativeDate(unixTimestamp) {
  const date = fromUnixTime(unixTimestamp);
  const now = new Date();

  if (isToday(date)) {
    return $t('our_donors.today');
  }

  if (isYesterday(date)) {
    return $t('our_donors.yesterday');
  }

  const days = differenceInDays(now, date);
  const months = differenceInMonths(now, date);
  const years = differenceInYears(now, date);

  if (years === 1) return `${years} ${$t('our_donors.one_year')}`;
  if (months === 1) return `${months} ${$t('our_donors.one_month')}`;

  if (years > 0) {
    return `${years} ${getWordForm(years, [
      $t('our_donors.x1_years'),
      $t('our_donors.x2_years'),
      $t('our_donors.x5_years'),
    ])}`;
  }

  if (months > 0) {
    return `${months} ${getWordForm(months, [
      $t('our_donors.x1_months'),
      $t('our_donors.x2_months'),
      $t('our_donors.x5_months'),
    ])}`;
  }

  return `${days} ${getWordForm(days, [
    $t('our_donors.x1_days'),
    $t('our_donors.x2_days'),
    $t('our_donors.x5_days'),
  ])}`;
}

function getWordForm(number, forms) {
  const cases = [2, 0, 1, 1, 1, 2];
  const index =
    number % 100 > 4 && number % 100 < 20 ? 2 : cases[Math.min(number % 10, 5)];
  return forms[index];
}
</script>
